import React from "react";

const STAT_GROUPS = [
  { key: "passing", label: "Passing", prefix: "passing" },
  { key: "rushing", label: "Rushing", prefix: "rushing" },
  { key: "receiving", label: "Receiving", prefix: "receiving" },
];

const SHORT_HEADERS = {
  passingpassAttempts: "Att",
  passingpassYards: "Yds",
  passingpassTD: "TD",
  rushingrushAttempts: "Att",
  rushingrushYards: "Yds",
  rushingrushTD: "TD",
  receivingtargets: "Tgt",
  receivingreceptions: "Rec",
  receivingrecYards: "Yds",
  receivingrecTD: "TD",
};

const INJURY_SHORT = {
  QUESTIONABLE: "Q",
  DOUBTFUL: "D",
  OUT: "O",
  "INJURED RESERVE": "IR",
  IR: "IR",
  PUP: "PUP",
  SUSPENDED: "SUS",
};

export function shareOf(part, total) {
  return total > 0 ? (part / total) * 100 : null;
}

export function toStatRow(node) {
  const stats = JSON.parse(node.stats);
  const player = node.player;
  const rushes = stats.rushingrushAttempts || 0;
  const targets = stats.receivingtargets || 0;
  const teamRushes = stats.teamRushes || 0;
  const teamPasses = stats.teamPasses || 0;

  return {
    ...stats,
    name: `${player.firstName} ${player.lastName}`,
    injuryStatus: player.injuryStatus,
    team: player.team.abbreviation,
    teamId: player.team.id,
    pos: player.position.name,
    rushMkt: shareOf(rushes, teamRushes),
    recMkt: shareOf(targets, teamPasses),
    totMkt: shareOf(rushes + targets, teamRushes + teamPasses),
  };
}

export function filterRows(rows, { positions, teamIds }) {
  return rows.filter(
    (row) =>
      (positions.length === 0 || positions.includes(row.pos)) &&
      (teamIds.length === 0 || teamIds.includes(row.teamId))
  );
}

export function injuryBadge(status) {
  const text = status ? String(status).trim().toUpperCase() : "";
  if (!text) {
    return null;
  }
  return {
    short: INJURY_SHORT[text] || text.charAt(0),
    label: text.charAt(0) + text.slice(1).toLowerCase(),
  };
}

export function visibleGroups(headers, rows) {
  const accessors = headers.map((header) => header.accessor);
  return STAT_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    accessors: accessors.filter((accessor) => accessor.startsWith(group.prefix)),
  })).filter(
    (group) =>
      group.accessors.length > 0 &&
      rows.some((row) =>
        group.accessors.some((accessor) => {
          const value = Number(row[accessor]);
          return !Number.isNaN(value) && value !== 0;
        })
      )
  );
}

function PercentCell({ value }) {
  return value === null || value === undefined ? "–" : `${value.toFixed(1)}%`;
}

function ShareCell({ value }) {
  if (value === null || value === undefined) {
    return "–";
  }
  return (
    <span className="nfl-share">
      <span>{value.toFixed(1)}%</span>
      <span className="nfl-share-bar" aria-hidden="true">
        <i style={{ width: `${Math.min(value, 100)}%` }} />
      </span>
    </span>
  );
}

export function buildStatEngineColumns(headers, rows, PlayerCell) {
  const headerFor = {};
  headers.forEach((header) => {
    headerFor[header.accessor] = header.Header;
  });

  return [
    {
      Header: "",
      id: "player-group",
      columns: [
        { Header: "Player", accessor: "name", id: "name", Cell: PlayerCell, sortDescFirst: false },
        { Header: "G", accessor: "starts" },
      ],
    },
    ...visibleGroups(headers, rows).map((group) => ({
      Header: group.label,
      id: group.key,
      columns: group.accessors.map((accessor) => ({
        Header: SHORT_HEADERS[accessor] || headerFor[accessor],
        accessor,
      })),
    })),
    {
      Header: "Market share",
      id: "share",
      columns: [
        { Header: "Rush", accessor: "rushMkt", Cell: PercentCell, sortType: shareSort },
        { Header: "Rec", accessor: "recMkt", Cell: PercentCell, sortType: shareSort },
        { Header: "Total", accessor: "totMkt", Cell: ShareCell, sortType: shareSort },
      ],
    },
  ];
}

export function zeroCellProps(cell) {
  return cell.value === 0 ? { className: "is-zero" } : {};
}

export function shareSort(rowA, rowB, columnId, desc) {
  const a = rowA.values[columnId];
  const b = rowB.values[columnId];

  // Both nulls compare as 0
  if (a === null && b === null) {
    return 0;
  }

  // A null always ends up after any number
  // When exactly one value is null, return a value that ends up positive
  // for the null row after react-table's negation for desc
  if (a === null) {
    return desc ? -1 : 1;
  }
  if (b === null) {
    return desc ? 1 : -1;
  }

  // Two numbers compare numerically
  return a > b ? 1 : a < b ? -1 : 0;
}
